// imageDerivatives — the browser makes the gallery sizes (ADR-068 §7: producer-
// made derivatives, no image processing in ems-app). One canvas resize per kind,
// JPEG, re-encoded down the quality ladder until it fits the kind's byte cap.
// Anything the browser cannot decode (HEIC on most desktops) yields null — the
// original still uploads; the gallery shows the file glyph instead of a thumb.

import { DERIVATIVE_KINDS, DERIVATIVE_MAX_BYTES, DERIVATIVE_MAX_EDGE, type DerivativeKind } from '@ems-app/schemas/files/vocabulary';

const QUALITY_LADDER = [0.85, 0.72, 0.6, 0.45];

async function decode(file: Blob): Promise<ImageBitmap | null> {
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return null;
  }
}

function encode(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
}

export async function makeDerivative(file: Blob, kind: DerivativeKind, bitmap?: ImageBitmap | null): Promise<Blob | null> {
  const bmp = bitmap ?? (await decode(file));
  if (!bmp) return null;
  const maxEdge = DERIVATIVE_MAX_EDGE[kind];
  const scale = Math.min(1, maxEdge / Math.max(bmp.width, bmp.height));
  const width = Math.max(1, Math.round(bmp.width * scale));
  const height = Math.max(1, Math.round(bmp.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(bmp, 0, 0, width, height);
  for (const q of QUALITY_LADDER) {
    const blob = await encode(canvas, q);
    if (blob && blob.size <= DERIVATIVE_MAX_BYTES[kind]) return blob;
  }
  return null;
}

/** Every kind the class accepts, from one decode. Missing kinds are simply absent. */
export async function makeDerivatives(file: Blob, kinds: readonly DerivativeKind[] = DERIVATIVE_KINDS): Promise<Partial<Record<DerivativeKind, Blob>>> {
  // No MIME guard: the OS often hands over `type: ''` (HEIC, some Android drag
  // sources) for what the server sniffs as an image; decode() decides.
  const out: Partial<Record<DerivativeKind, Blob>> = {};
  const bmp = await decode(file);
  if (!bmp) return out;
  try {
    for (const kind of kinds) {
      const blob = await makeDerivative(file, kind, bmp);
      if (blob) out[kind] = blob;
    }
  } finally {
    bmp.close();
  }
  return out;
}
